import { useCallback, useEffect, useMemo } from "react"
import ReactFlow, {
  Background,
  BackgroundVariant,
  Controls,
  Handle,
  Position,
  ReactFlowProvider,
  useNodesInitialized,
  useReactFlow,
  type Connection,
  type Edge,
  type Node,
  type NodeProps,
  type OnNodesChange,
} from "reactflow"

// ReactFlow ships its own CSS that defines `.react-flow__pane` interaction
// rules (pointer events, positioning, viewport transforms). Without this
// import, the canvas renders nodes but clicks never reach the pane. Other
// routes that mount IVR (e.g. the agent page) happen to import this CSS
// elsewhere, so the issue only surfaced in standalone hosts.
import "reactflow/dist/style.css"

import { Tooltip } from "@coval/ui/tooltip"
import { X } from "lucide-react"

interface IVRNodeData {
  id: string
  prompt: string
  selected?: boolean
  terminal?: boolean
  hasBranches?: boolean
  autoAdvance?: boolean
  onDelete?: (id: string) => void
}

const truncate = (text: string, max: number) =>
  text.length > max ? `${text.slice(0, max)}…` : text

const NodeShell = ({
  data,
  variant,
}: {
  data: IVRNodeData
  variant: "start" | "process" | "decision" | "terminal"
}) => {
  const variantClass = {
    start: "border-coval-info-border bg-coval-info-surface",
    process: "border-coval-border bg-white dark:bg-gray-900",
    decision: "border-amber-400 bg-amber-50 dark:bg-amber-950/40",
    terminal: "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/40",
  }[variant]

  const selectedRing = data.selected
    ? "ring-2 ring-coval-border-active ring-offset-1"
    : ""

  return (
    <div className="relative group">
      <Handle
        type="target"
        position={Position.Top}
        className="!w-2 !h-2 !bg-coval-text-muted"
      />
      <div
        className={`min-w-[180px] max-w-[220px] rounded-md border-2 px-3 py-2 text-left shadow-sm transition-shadow ${variantClass} ${selectedRing}`}
      >
        <div className="text-[10px] font-mono uppercase tracking-wide text-coval-text-muted">
          {data.id}
        </div>
        <div className="text-xs text-coval-text mt-1 leading-snug">
          {truncate(data.prompt || "(no prompt)", 90)}
        </div>
        <div className="mt-1 flex flex-wrap gap-1">
          {variant === "start" && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-coval-info-surface text-coval-info-on-surface font-medium">
              start
            </span>
          )}
          {variant === "decision" && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 font-medium">
              branches
            </span>
          )}
          {variant === "terminal" && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-200 text-emerald-900 font-medium">
              terminal
            </span>
          )}
          {data.autoAdvance && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-surface-2 text-coval-text font-medium">
              auto
            </span>
          )}
        </div>
      </div>
      {data.onDelete && (
        <Tooltip content={`Delete node ${data.id}`}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              data.onDelete?.(data.id)
            }}
            aria-label={`Delete node ${data.id}`}
            className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-coval-danger-surface text-coval-danger-on-surface border border-coval-danger-border opacity-0 group-hover:opacity-100 hover:bg-coval-danger-surface-hover transition-opacity flex items-center justify-center shadow-sm"
          >
            <X className="w-3 h-3" />
          </button>
        </Tooltip>
      )}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-2 !h-2 !bg-coval-text-muted"
      />
    </div>
  )
}

const StartNode = ({ data }: NodeProps<IVRNodeData>) => (
  <NodeShell data={data} variant="start" />
)
const ProcessNode = ({ data }: NodeProps<IVRNodeData>) => (
  <NodeShell data={data} variant="process" />
)
const DecisionNode = ({ data }: NodeProps<IVRNodeData>) => (
  <NodeShell data={data} variant="decision" />
)
const TerminalNode = ({ data }: NodeProps<IVRNodeData>) => (
  <NodeShell data={data} variant="terminal" />
)

const nodeTypes = {
  start: StartNode,
  process: ProcessNode,
  decision: DecisionNode,
  terminal: TerminalNode,
}

interface IVRFlowPreviewProps {
  nodes: Node[]
  edges: Edge[]
  selectedNodeId?: string | null
  onSelectNode?: (id: string | null) => void
  onConnect?: (source: string, target: string) => void
  onDeleteNode?: (id: string) => void
  onPositionChange?: (id: string, position: { x: number; y: number }) => void
}

/** Re-fits the viewport once nodes have been measured. ReactFlow's `fitView`
 * prop only runs once on mount; if the container or nodes weren't measured
 * yet (initial-render race in narrow editor layouts), the viewport stays
 * stuck at whatever bad bounds it captured. Listening to
 * `useNodesInitialized` lets us run fitView at the right time. */
const AutoFitOnReady: React.FC<{ nodeCount: number }> = ({ nodeCount }) => {
  const initialized = useNodesInitialized()
  const { fitView } = useReactFlow()

  useEffect(() => {
    if (!initialized || nodeCount === 0) {
      return
    }
    const refit = () => fitView({ padding: 0.2, duration: 0 })
    // Immediate + deferred fires cover both fast and slow layout settles.
    refit()
    const t = setTimeout(refit, 200)
    // Re-fit on container resize — keeps the viewport sane if the parent
    // ever changes width (e.g. side panel collapses, dialog animates).
    const pane = document.querySelector(".react-flow__pane")
    if (!(pane instanceof Element)) {
      return () => clearTimeout(t)
    }
    const observer = new ResizeObserver(refit)
    observer.observe(pane)
    return () => {
      clearTimeout(t)
      observer.disconnect()
    }
  }, [initialized, nodeCount, fitView])
  return null
}

const Canvas: React.FC<IVRFlowPreviewProps> = ({
  nodes,
  edges,
  selectedNodeId,
  onSelectNode,
  onConnect,
  onDeleteNode,
  onPositionChange,
}) => {
  const styledNodes = useMemo(
    () =>
      nodes.map((node) => ({
        ...node,
        data: {
          ...(node.data as IVRNodeData),
          selected: node.id === selectedNodeId,
          onDelete: onDeleteNode,
        },
      })),
    [nodes, selectedNodeId, onDeleteNode],
  )

  const styledEdges = useMemo(
    () =>
      edges.map((edge) => ({
        ...edge,
        labelStyle: {
          fontSize: 10,
          fill: "var(--coval-text-muted)",
          fontWeight: 500,
        },
        labelBgStyle: { fill: "var(--surface-default)", fillOpacity: 0.9 },
        labelBgPadding: [4, 2] as [number, number],
        labelBgBorderRadius: 4,
      })),
    [edges],
  )

  const handleConnect = useCallback(
    (connection: Connection) => {
      if (!onConnect || !connection.source || !connection.target) {
        return
      }
      onConnect(connection.source, connection.target)
    },
    [onConnect],
  )

  const handleNodesChange = useCallback<OnNodesChange>(
    (changes) => {
      if (!onPositionChange) {
        return
      }
      for (const change of changes) {
        if (
          change.type === "position" &&
          change.dragging === false &&
          change.position
        ) {
          onPositionChange(change.id, change.position)
        }
      }
    },
    [onPositionChange],
  )

  return (
    <div className="relative w-full h-full rounded-lg border border-coval-border bg-surface-1 overflow-hidden">
      <ReactFlow
        nodes={styledNodes}
        edges={styledEdges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        proOptions={{ hideAttribution: true }}
        nodesDraggable={!!onPositionChange}
        nodesConnectable={!!onConnect}
        elementsSelectable={!!onSelectNode}
        onNodeClick={(_, node) => onSelectNode?.(node.id)}
        onPaneClick={() => onSelectNode?.(null)}
        onConnect={handleConnect}
        onNodesChange={handleNodesChange}
        defaultEdgeOptions={{ style: { strokeWidth: 1.5 } }}
      >
        <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
        <Controls showInteractive={false} />
        <AutoFitOnReady nodeCount={styledNodes.length} />
      </ReactFlow>
    </div>
  )
}

export const IVRFlowPreview: React.FC<IVRFlowPreviewProps> = (props) => (
  <ReactFlowProvider>
    <Canvas {...props} />
  </ReactFlowProvider>
)
